// @react
import React from 'react'
import { ReactElement, useState } from 'react'
// @graphql
import { useQuery } from '@apollo/client'
import {
  FilterOrder,
  GetUserContentQuery,
  GetUserContentQueryVariables,
  UserContentOrderByList,
  UserContentRelationship,
} from 'lib/graphql/graphqlTypes'
import { GET_USER_CONTENT } from 'graphql/queries/userContent.query'
// @common
import usePagination from 'modules/common/hooks/usePagination'
// @libraries
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
// @components
import Button from '@bit/atd.web.button'
import Spacer from '@bit/atd.web.spacer'
import Icon from '@bit/atd.web.icons'
import icons from '@bit/atd.web.icons/getIcons'
import Grid from '@bit/atd.web.grid'
// @module components
import PaginationWrapper from 'modules/common/components/PaginationWrapper'
import ContentCard from 'modules/common/components/ContentCard'
import ConditionalLoader from '@bit/atd.web.loaders.conditional-loader'
import QueryError from 'modules/common/components/Errors/QueryError'
import PaginationScrollWrapper from 'modules/common/components/PaginationScrollWrapper'
import strings from 'lib/constants/strings'

/**
 * 4.0.4.1 MyATD - Activity History - User Content
 * UX: https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3233%3A6661
 */
const useStyles = makeStyles({
  textTitle: {
    color: '#2E2d31',
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '26px',
    marginBottom: '8px',
  },
  textDescription: {
    color: '#424140',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
  },
})
const UserContent = (): ReactElement => {
  /******************************************
   * STATE
   ******************************************/

  const [sortOrder, setSortOrder] = useState<FilterOrder>(FilterOrder.ASC)

  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()

  const { Pagination, paginationProps } = usePagination()

  const { loading: isLoading, error: isError, data } = useQuery<
    GetUserContentQuery,
    GetUserContentQueryVariables
  >(GET_USER_CONTENT, {
    variables: {
      input: {
        page: paginationProps.currentPage,
        filters: {
          userRelationship: UserContentRelationship.AUTHORED,
        },
        orderBy: {
          order: sortOrder,
          value: UserContentOrderByList.PUBLISHED_DATE,
        },
      },
    },
  })

  /******************************************
   * FUNCTIONS
   ******************************************/

  /**
   *
   */
  const toggleSortHandler = () => {
    const newOrder =
      sortOrder === FilterOrder.ASC ? FilterOrder.DESC : FilterOrder.ASC
    setSortOrder(newOrder)
  }

  /**
   *
   */
  const getOrderGlyph = (): keyof typeof icons => {
    return sortOrder === FilterOrder.ASC ? 'caret-down' : 'caret-up'
  }

  /******************************************
   * RENDER
   ******************************************/

  if (isError || (!data && !isLoading)) {
    return <QueryError />
  }

  return (
    <ConditionalLoader condition={!isLoading}>
      {data?.userContent.data.length ? (
        <PaginationScrollWrapper {...paginationProps}>
          <Grid container>
            <Grid item>
              <span>Sort by: </span>
              <Button
                onClick={toggleSortHandler}
                variant="secondary"
                size="small"
              >
                <>
                  Date: {sortOrder === FilterOrder.ASC ? 'Newest' : 'Oldest'}
                  <Spacer size={4} />
                  <Icon glyph={getOrderGlyph()} size={16} />
                </>
              </Button>
            </Grid>
          </Grid>

          <Spacer fullWidth size={30} />

          <Grid container spacing={3}>
            {data?.userContent?.data?.map((item) => {
              return (
                <Grid key={`${item.__typename}_${item.id}`} item xs={12}>
                  <ContentCard
                    typename={item.__typename}
                    // @ts-ignore
                    item={item}
                  />
                </Grid>
              )
            })}
          </Grid>

          <PaginationWrapper>
            <Pagination
              {...paginationProps}
              count={data.userContent?.meta.totalPages}
            />
          </PaginationWrapper>
        </PaginationScrollWrapper>
      ) : (
        <div>
          <Typography className={classes.textTitle} component={'h3'}>
            {strings.ACTIVITY_HISTORY.USER_CONTENT.NO_RESULTS_MESSAGE}
          </Typography>
          <Typography className={classes.textDescription} component={'p'}>
            {strings.ACTIVITY_HISTORY.USER_CONTENT.NO_RESULTS_DESCRIPTION}
          </Typography>
        </div>
      )}
    </ConditionalLoader>
  )
}

export default UserContent
